import { useMemo } from 'react';
import { useSubscriptionPlans } from 'assets/js/pages/account-settings/account-about/givebutter-plus/useSubscriptionPlans';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { BillingCycleCost, GivebutterPlusPlan, Subscription } from 'types/domain';

const DEFAULT_PLAN: [boolean, number] = [false, 0];

const useRecommendedPlanComparison = () => {
    const { validGivebutterPlusPlan } = useCurrentAccount();
    const { data: plans } = useSubscriptionPlans();

    const [isRecommendedPlanCheaper, recommendedPlanDifference] = useMemo(() => {
        if (!validGivebutterPlusPlan) {
            return DEFAULT_PLAN;
        }

        const currentTier = validGivebutterPlusPlan.tier;

        const currentPlan = findCurrentPlanFromTier(plans, currentTier);
        const recommendedPlan = findRecommendedPlan(plans);

        if (!currentPlan || !recommendedPlan || currentPlan.is_recommend) {
            return DEFAULT_PLAN;
        }

        const compareAnnual = currentTier.interval === 'year';

        const currentBillingCosts = compareAnnual
            ? currentPlan.billing.yearly_billing_cycle
            : currentPlan.billing.monthly_billing_cycle;
        const recommendedBillingCosts = compareAnnual
            ? recommendedPlan.billing.yearly_billing_cycle
            : recommendedPlan.billing.monthly_billing_cycle;

        const difference = getCostDifferenceBetweenPlans(
            currentBillingCosts,
            recommendedBillingCosts,
            compareAnnual,
        );

        const isCheaper = difference > 0;

        return [isCheaper, difference];
    }, [validGivebutterPlusPlan, plans]);
    return {
        isRecommendedPlanCheaper,
        recommendedPlanDifference,
    };
};

const getCostDifferenceBetweenPlans = (
    currentPlanCost: BillingCycleCost,
    newPlanCost: BillingCycleCost,
    compareAnnual: boolean,
) => {
    if (compareAnnual) {
        return currentPlanCost.perYear - newPlanCost.perYear;
    }

    return currentPlanCost.perMonth - newPlanCost.perMonth;
};

const findCurrentPlanFromTier = (
    plans: GivebutterPlusPlan[],
    currentTier: Subscription['tier'],
) => {
    return plans.find(plan => plan.contacts_max === currentTier.max_contacts);
};
const findRecommendedPlan = (plans: GivebutterPlusPlan[]) => {
    return plans.find(plan => plan.is_recommend);
};

export { useRecommendedPlanComparison, getCostDifferenceBetweenPlans };
